import React from 'react'
import { twMerge } from 'tailwind-merge'

import { usePressRepeat } from '../../usePressRepeat'
import { Box } from '../box'
import {
    CalendarGlyph,
    ChevronDownGlyph,
    ChevronUpGlyph,
    GLYPH_COLORS,
} from '../picker-icons/pickerGlyphs'
import { Pressable } from '../pressable'
import { Text } from '../text'

export type TimePickerProps = {
    /** Hours, 0–23. */
    hours: number
    /** Minutes, 0–59. */
    minutes: number
    /** Step applied by the chevrons. */
    hourStep?: number
    minuteStep?: number
    /** Called with the next wrapped value on press, then repeatedly while a chevron is held. */
    onHoursChange?: (hours: number) => void
    onMinutesChange?: (minutes: number) => void
    /**
     * Renders the calendar toggle above the spinners (switch to date).
     * Defaults to rendering only when `onCalendarPress` is provided.
     */
    showCalendarIcon?: boolean
    onCalendarPress?: () => void
    isDisabled?: boolean
    className?: string
}

const pad2 = (value: number): string => value.toString().padStart(2, '0')
const wrap = (value: number, max: number): number => ((value % max) + max) % max

const chevron = 'size-6 items-center justify-center'
const numberText = 'text-content-default text-[3.247rem] leading-[120%] text-center'
// Non-translatable glyph kept as a constant so it reads as an expression
// (not a bare JSX literal — `react/jsx-no-literals`).
const SEPARATOR = ':'

type SpinnerProps = {
    value: string
    onUp?: () => void
    onDown?: () => void
    isDisabled?: boolean
    label: string
}

const Spinner: React.FC<SpinnerProps> = ({ value, onUp, onDown, isDisabled, label }) => {
    const up = usePressRepeat(isDisabled ? undefined : onUp)
    const down = usePressRepeat(isDisabled ? undefined : onDown)

    return (
        <Box className='flex-col items-center justify-center gap-2'>
            <Pressable
                className={chevron}
                {...up}
                accessibilityRole='button'
                accessibilityLabel={`Increase ${label}`}
            >
                <ChevronUpGlyph size={24} color={GLYPH_COLORS.default} />
            </Pressable>
            <Text className={numberText}>{value}</Text>
            <Pressable
                className={chevron}
                {...down}
                accessibilityRole='button'
                accessibilityLabel={`Decrease ${label}`}
            >
                <ChevronDownGlyph size={24} color={GLYPH_COLORS.default} />
            </Pressable>
        </Box>
    )
}

/**
 * Hours / minutes spinner matching the Figma redesign (AOS-617). Presentational
 * and controlled — chevrons emit the next wrapped value via `onHoursChange` /
 * `onMinutesChange`; the parent owns the time. Optional calendar affordance
 * switches the host picker back to date mode.
 */
export const TimePicker: React.FC<TimePickerProps> = ({
    hours,
    minutes,
    hourStep = 1,
    minuteStep = 1,
    onHoursChange,
    onMinutesChange,
    showCalendarIcon,
    onCalendarPress,
    isDisabled,
    className,
}) => (
    <Box
        className={twMerge(
            'flex-col items-center justify-center gap-3 py-8 w-[256px]',
            isDisabled ? 'opacity-40' : '',
            className,
        )}
    >
        {(showCalendarIcon ?? !!onCalendarPress) ? (
            <Pressable
                className='size-6 items-center justify-center'
                onPress={isDisabled ? undefined : onCalendarPress}
                accessibilityRole='button'
                accessibilityLabel='Switch to date'
            >
                <CalendarGlyph size={24} color={GLYPH_COLORS.accent} />
            </Pressable>
        ) : null}

        <Box className='flex-row items-center gap-4'>
            <Spinner
                label='hours'
                value={pad2(hours)}
                isDisabled={isDisabled}
                onUp={() => onHoursChange?.(wrap(hours + hourStep, 24))}
                onDown={() => onHoursChange?.(wrap(hours - hourStep, 24))}
            />
            <Text className={numberText}>{SEPARATOR}</Text>
            <Spinner
                label='minutes'
                value={pad2(minutes)}
                isDisabled={isDisabled}
                onUp={() => onMinutesChange?.(wrap(minutes + minuteStep, 60))}
                onDown={() => onMinutesChange?.(wrap(minutes - minuteStep, 60))}
            />
        </Box>
    </Box>
)

TimePicker.displayName = 'TimePicker'
